import type { AxiosRequestTransformer } from 'axios';

import { isTauri } from '@/tauri/utils';
import { isOnPrem } from '@/utils/buildProfile';

export const ON_PREM_OPTIONAL_METADATA_HEADERS = [
  'X-Client-Id',
  'X-Client-Version',
  'X-Device-Id',
  'X-Device-Session-Id',
  'Sentry-Trace',
  'Baggage',
  'Traceparent',
  'Tracestate',
  'X-Workspace-Id',
  'Heidi-Workspace-Id',
  'Heidi-Session-Id',
  'X-Request-Id',
  'X-Correlation-Id',
  'X-Temp-User-ID',
] as const;

const ON_PREM_OPTIONAL_METADATA_HEADER_NAMES = new Set(
  ON_PREM_OPTIONAL_METADATA_HEADERS.map((header) => header.toLowerCase())
);

/**
 * True only in the on-prem desktop app — the one deployment where API traffic
 * is cross-origin (the Tauri WebView origin is not the hospital server) and
 * must survive the gateway's deliberately small CORS policy. The on-prem web
 * build is served same-origin by the ingress, so it needs no adaptation, and
 * `isOnPrem` is a build-time constant, so cloud bundles fold this to `false`.
 */
export const isOnPremRuntime = (): boolean => isOnPrem && isTauri();

/** Remove optional metadata from a plain header record used by raw fetch/SSE. */
export const omitOnPremOptionalMetadataHeaders = <T extends Record<string, string>>(
  headers: T
): T => {
  if (!isOnPremRuntime()) return headers;

  const sanitized = { ...headers };
  for (const header of Object.keys(sanitized)) {
    if (ON_PREM_OPTIONAL_METADATA_HEADER_NAMES.has(header.toLowerCase())) {
      delete sanitized[header];
    }
  }
  return sanitized;
};

/**
 * On-prem gateways intentionally allow only essential CORS request headers.
 * Axios runs request transforms after request interceptors, so this removes
 * optional metadata even when an interceptor from a compiled package added it.
 */
export const stripOnPremOptionalMetadataHeaders: AxiosRequestTransformer = (
  data: unknown,
  headers
) => {
  if (isOnPremRuntime()) {
    for (const header of ON_PREM_OPTIONAL_METADATA_HEADERS) {
      headers.delete(header);
    }
  }
  return data;
};
